import React, { PropsWithChildren, useMemo } from 'react';
import {
    CoreApi,
    CoreApiProvider,
    ResidentialUnitTooltipContext,
    ResidentialUnitTooltipContextData,
} from '@ygtechnologies/bygr-frontend-component-library';
import { getResidentialUnitExtraData } from '@/services/residential-units';
import { useCurrentCulture, useGetProjectId } from '@/utils/hooks';
import { getUserAvatarThumbnailUrl } from '@/services/users';
import {
    ApiService,
    ChatProvider,
    ChatProviderData,
    ChatService,
    CommentThreadType,
} from '@ygtechnologies/bygr-frontend-chat';
import { getChatConnectionManager } from '@/server-events/connections';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { commentEventMapping } from '@/components/Comments/CommentListItemViews';
import ChangeRequestTooltip from '@/seller/components/data-tooltips/ChangeRequestTooltip';
import { HeaderContextProvider } from '@/components/layout/Header';
import { LocalizationForwarder } from './LocalizationForwarder';
import { ThemePreferenceProvider } from './ThemePreferenceContext';

export interface ContextsProps extends PropsWithChildren {}

export const Contexts: React.FC<ContextsProps> = ({ children }) => {
    const { user, activeProfile, activeProject } = useSelector((state: IState) => state.global);
    const [cultureInfo] = useCurrentCulture();
    const projectId = useGetProjectId();

    const unitTooltipContext: ResidentialUnitTooltipContextData = useMemo(
        () => ({
            getData: (unitId) => {
                return getResidentialUnitExtraData(unitId) as any; //TODO:Fix typing
            },
            getUrl: (unitId) => `/projects/${activeProject?.id}/residential-units/${unitId}`,
            getAvatarThumbnailUrl: (userId) => getUserAvatarThumbnailUrl(userId),
        }),
        [activeProject]
    );

    const chatContextData = useMemo<ChatProviderData>(() => {
        const apiService = new ApiService(window.APP_CONFIG.API_CHAT_URL);

        return {
            chatService: new ChatService(apiService),
            hubConnection: getChatConnectionManager(),
            currentUserId: user?.userId ?? null,
            currentProfileId: activeProfile?.id ?? null,
            eventMapping: commentEventMapping,
            threadTitleOverride: ({ type, threadName, entityId }) => {
                if (type === CommentThreadType.ChangeRequest && entityId) {
                    return (
                        <ChangeRequestTooltip changeRequestId={entityId}>
                            <span>{threadName}</span>
                        </ChangeRequestTooltip>
                    );
                }
                return threadName;
            },
        };
    }, [user, activeProfile]);

    return (
        <ThemePreferenceProvider>
            <ChatProvider value={chatContextData} languageCode={cultureInfo!.languageCode}>
                <HeaderContextProvider>
                    <LocalizationForwarder>
                        <ResidentialUnitTooltipContext.Provider value={unitTooltipContext}>
                            <CoreApiProvider
                                profileId={activeProfile?.id ?? null}
                                projectId={projectId ? projectId : null}
                                api={new CoreApi()}>
                                {children}
                            </CoreApiProvider>
                        </ResidentialUnitTooltipContext.Provider>
                    </LocalizationForwarder>
                </HeaderContextProvider>
            </ChatProvider>
        </ThemePreferenceProvider>
    );
};
